import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Stack,
  Switch,
  Autocomplete,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  NotificationsActive as NotifyIcon,
} from '@mui/icons-material';
import { OPS_COLORS } from '../../constants/operationsDashboardColors';
import { apiClient } from '../../services/api';
import CoverageHistoryPanel from './CoverageHistoryPanel';
import {
  ABSENCE_REASON_OPTIONS,
  CoverageModeOption,
  applyCoverageStatusToForm,
  coverageStatusRequiresCoveringAgent,
  deriveCoverageTypeAndReason,
  usesOriginalScheduleForCoverageStatus,
} from '../../utils/coverageDashboardUtils';
import { getAttendanceStatusLabel } from '../../utils/attendanceLabels';

export interface CoverageFormState {
  absentEmployeeId: string;
  absentAgentName: string;
  campaignId: string;
  campaignName: string;
  workDate: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  attendanceStatus: string;
  currentCoverageStatus: string;
  absenceReason: string;
  coveringEmployeeId: string;
  coverageType: string;
  coverageReason: string;
  coverageStartTime: string;
  coverageEndTime: string;
  coverageMode: CoverageModeOption;
  coverageStatus: string;
  notes: string;
  customerNotified: boolean;
  coverageAssignmentId?: string | null;
  coveredByName?: string | null;
  coveringFor?: string[];
}

interface CoverageAgentOption {
  userId: string;
  label: string;
  warning?: string;
  campaignName?: string;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  isCrossCampaign?: boolean;
}

interface CoverageAssignModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (form: CoverageFormState) => Promise<void>;
  form: CoverageFormState;
  setForm: React.Dispatch<React.SetStateAction<CoverageFormState>>;
  loading?: boolean;
  mode: 'assign' | 'change' | 'note' | 'remove' | 'not_required';
}

const COVERAGE_STATUSES = ['COVERED', 'PARTIALLY_COVERED', 'NOT_COVERED', 'NOT_REQUIRED'];

function calcDurationLabel(start?: string, end?: string): string {
  if (!start || !end) return '—';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startM = sh * 60 + sm;
  let endM = eh * 60 + em;
  if (endM < startM) endM += 24 * 60;
  const total = Math.max(0, endM - startM);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
}

const CoverageAssignModal: React.FC<CoverageAssignModalProps> = ({
  open,
  onClose,
  onSave,
  form,
  setForm,
  loading,
  mode,
}) => {
  const [agentOptions, setAgentOptions] = useState<CoverageAgentOption[]>([]);
  const [selectedWarning, setSelectedWarning] = useState<string | undefined>();
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentOptionsError, setAgentOptionsError] = useState<string | null>(null);
  const [coveringAgentError, setCoveringAgentError] = useState<string | null>(null);

  const [transferConfirmOpen, setTransferConfirmOpen] = useState(false);

  const durationLabel = useMemo(
    () => calcDurationLabel(form.coverageStartTime, form.coverageEndTime),
    [form.coverageStartTime, form.coverageEndTime]
  );

  const selectedAgent = useMemo(
    () => agentOptions.find((opt) => opt.userId === form.coveringEmployeeId) || null,
    [agentOptions, form.coveringEmployeeId],
  );

  const needsFullTransferConfirm = Boolean(
    form.coverageMode === 'FULL_TRANSFER'
    && selectedAgent
    && (selectedAgent.isCrossCampaign || (selectedAgent.campaignName && selectedAgent.campaignName !== form.campaignName && selectedAgent.campaignName !== 'N/A'))
  );

  useEffect(() => {
    if (!open || mode === 'note' || mode === 'remove' || mode === 'not_required') return;
    const load = async () => {
      setLoadingAgents(true);
      setAgentOptionsError(null);
      try {
        const response = await apiClient.operations.getCoverageAgentOptions({
          date: form.workDate,
          campaignId: form.campaignId,
          absentEmployeeId: form.absentEmployeeId,
        });
        setAgentOptions(response.data.data || []);
      } catch (err: any) {
        setAgentOptions([]);
        setAgentOptionsError(err?.response?.data?.message || 'Failed to load available covering agents.');
      } finally {
        setLoadingAgents(false);
      }
    };
    load();
  }, [open, form.workDate, form.campaignId, form.absentEmployeeId, mode]);

  useEffect(() => {
    if (!open) {
      setCoveringAgentError(null);
      setAgentOptionsError(null);
    }
  }, [open]);

  useEffect(() => {
    const selected = agentOptions.find((opt) => opt.userId === form.coveringEmployeeId);
    setSelectedWarning(selected?.warning);
  }, [agentOptions, form.coveringEmployeeId]);

  const titleMap = {
    assign: (!form.coverageAssignmentId && form.currentCoverageStatus === 'PARTIALLY_COVERED')
      ? 'Add Coverage'
      : 'Assign Coverage',
    change: 'Change Coverage',
    note: 'Add Note',
    remove: 'Remove Coverage',
    not_required: 'Mark No Coverage Needed',
  };

  const notesRequired = mode === 'remove' || mode === 'not_required';
  const showCoverageFields = mode === 'assign' || mode === 'change';
  const requiresCoveringAgent = showCoverageFields && coverageStatusRequiresCoveringAgent(form.coverageStatus);
  const useOriginalSchedule = usesOriginalScheduleForCoverageStatus(form.coverageStatus);
  const showCoverageTimes = ['COVERED', 'PARTIALLY_COVERED'].includes(form.coverageStatus);

  const handleSaveClick = () => {
    if (requiresCoveringAgent && !form.coveringEmployeeId) {
      setCoveringAgentError('Select an agent in Covered By before saving.');
      return;
    }
    setCoveringAgentError(null);
    if (needsFullTransferConfirm) {
      setTransferConfirmOpen(true);
      return;
    }
    onSave(form);
  };

  const handleConfirmFullTransfer = () => {
    setTransferConfirmOpen(false);
    onSave(form);
  };

  const handleCoverageStatusChange = (coverageStatus: string) => {
    setForm(applyCoverageStatusToForm(form, coverageStatus, {
      keepPartialTimes: coverageStatus === 'PARTIALLY_COVERED' && form.coverageStatus === 'PARTIALLY_COVERED',
    }));
  };

  const handleCoverageModeChange = (coverageMode: CoverageModeOption) => {
    const derived = deriveCoverageTypeAndReason(form.coverageStatus, coverageMode);
    setForm({ ...form, coverageMode, ...derived });
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{titleMap[mode]}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Absent Agent" value={form.absentAgentName} disabled />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Campaign" value={form.campaignName} disabled />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Scheduled Shift" value={`${form.scheduledStartTime || '-'} - ${form.scheduledEndTime || '-'}`} disabled />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Attendance Status" value={getAttendanceStatusLabel(form.attendanceStatus)} disabled />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Coverage Status" value={form.currentCoverageStatus.replace(/_/g, ' ')} disabled />
          </Grid>

          {(mode === 'assign' || mode === 'change' || mode === 'not_required') && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Absence Reason</InputLabel>
                <Select
                  value={form.absenceReason || 'PENDING_SUPERVISOR_REVIEW'}
                  label="Absence Reason"
                  onChange={(e) => setForm({ ...form, absenceReason: e.target.value })}
                >
                  {ABSENCE_REASON_OPTIONS.map((r) => (
                    <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Choose a specific reason to record an approved absence. Leave as Pending to allow automatic No Show at 30 min.
                </Typography>
              </FormControl>
            </Grid>
          )}

          {form.coveredByName && (
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>{form.absentAgentName}</Typography>
                <Typography variant="caption">↓ Covered By</Typography>
                <Typography variant="body2">{form.coveredByName}</Typography>
              </Box>
            </Grid>
          )}

          {showCoverageFields && (
            <>
              <Grid item xs={12}>
                <Autocomplete
                  options={agentOptions}
                  value={selectedAgent}
                  onChange={(_, value) => {
                    setCoveringAgentError(null);
                    setForm({ ...form, coveringEmployeeId: value?.userId || '' });
                  }}
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
                  loading={loadingAgents}
                  disabled={loadingAgents}
                  noOptionsText={loadingAgents ? 'Loading agents…' : 'No matching agents'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Covered By"
                      required
                      error={Boolean(coveringAgentError)}
                      helperText={
                        coveringAgentError
                        || 'Search by name, status, campaign, or clock-in time'
                      }
                      placeholder={loadingAgents ? 'Loading agents…' : 'Search covering agent'}
                    />
                  )}
                />
                {!loadingAgents && !agentOptionsError && agentOptions.length === 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                    No covering agents found. Check that other employees are scheduled or active today.
                  </Typography>
                )}
                {agentOptionsError && (
                  <Alert severity="error" sx={{ mt: 1, py: 0 }}>{agentOptionsError}</Alert>
                )}
                {selectedWarning && <Alert severity="warning" sx={{ mt: 1 }}>{selectedWarning}</Alert>}
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Coverage Mode</InputLabel>
                  <Select
                    value={form.coverageMode}
                    label="Coverage Mode"
                    onChange={(e) => handleCoverageModeChange(e.target.value as CoverageModeOption)}
                  >
                    <MenuItem value="FULL_TRANSFER">Full Transfer</MenuItem>
                    <MenuItem value="SHARED_COVERAGE">Shared Coverage / Queue Support</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Coverage Status</InputLabel>
                  <Select
                    value={form.coverageStatus}
                    label="Coverage Status"
                    onChange={(e) => handleCoverageStatusChange(e.target.value)}
                  >
                    {COVERAGE_STATUSES.map((status) => <MenuItem key={status} value={status}>{status.replace(/_/g, ' ')}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Coverage Type"
                  value={form.coverageType.replace(/_/g, ' ')}
                  disabled
                  helperText="Set automatically from coverage status and mode"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Coverage Reason"
                  value={form.coverageReason.replace(/_/g, ' ')}
                  disabled
                  helperText="Set automatically from coverage status and mode"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {form.coverageMode === 'FULL_TRANSFER'
                    ? 'Full transfer temporarily moves the covering agent to the absent agent campaign.'
                    : 'Shared coverage keeps the covering agent on their original campaign while supporting another queue.'}
                </Alert>
              </Grid>
              {showCoverageTimes && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      {useOriginalSchedule
                        ? 'Covered uses the absent agent original scheduled shift.'
                        : 'Partially covered uses custom times. Start defaults to now; adjust as needed.'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Start"
                      value={form.coverageStartTime}
                      onChange={(e) => setForm({ ...form, coverageStartTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={useOriginalSchedule}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="time"
                      label="End"
                      value={form.coverageEndTime}
                      onChange={(e) => setForm({ ...form, coverageEndTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={useOriginalSchedule}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth label="Coverage Duration" value={durationLabel} disabled />
                  </Grid>
                </>
              )}
            </>
          )}

          {(mode === 'assign' || mode === 'change') && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  border: '2px solid',
                  borderColor: form.customerNotified ? OPS_COLORS.present : OPS_COLORS.uncovered,
                  bgcolor: form.customerNotified ? `${OPS_COLORS.present}12` : `${OPS_COLORS.uncovered}14`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Box sx={{ mt: 0.25, color: form.customerNotified ? OPS_COLORS.present : OPS_COLORS.uncovered }}>
                  {form.customerNotified ? <CheckCircleIcon /> : <NotifyIcon />}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color={form.customerNotified ? 'success.dark' : 'warning.dark'}
                      >
                        Customer notified about coverage
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {form.customerNotified
                          ? 'Client/customer has been informed about this coverage change.'
                          : 'Confirm the client/customer has been informed before saving.'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={form.customerNotified}
                      onChange={(e) => setForm({ ...form, customerNotified: e.target.checked })}
                      color="success"
                      inputProps={{ 'aria-label': 'Customer notified about coverage' }}
                    />
                  </Stack>
                </Box>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={notesRequired ? 'Notes (required)' : 'Notes (optional)'}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              required={notesRequired}
            />
          </Grid>

          {mode === 'remove' && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Removing coverage will mark this absence as uncovered unless reassigned.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <CoverageHistoryPanel assignmentId={form.coverageAssignmentId} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={mode === 'remove' ? 'error' : 'primary'}
          onClick={handleSaveClick}
          disabled={
            loading
            || (notesRequired && !form.notes.trim())
            || (requiresCoveringAgent && !form.coveringEmployeeId)
            || loadingAgents
          }
        >
          {loading ? 'Saving...' : mode === 'remove' ? 'Remove Coverage' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={transferConfirmOpen} onClose={() => setTransferConfirmOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm Full Transfer</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 1 }}>
          {selectedAgent
            ? `${selectedAgent.label.split(' — ')[0]} is currently assigned to ${selectedAgent.campaignName || 'another campaign'}`
              + (selectedAgent.scheduledStartTime && selectedAgent.scheduledEndTime
                ? ` (${selectedAgent.scheduledStartTime}–${selectedAgent.scheduledEndTime})`
                : '')
              + `. Assigning FULL TRANSFER will transfer active work from that campaign → ${form.campaignName}`
              + (form.coverageStartTime ? ` effective ${form.coverageStartTime}` : '')
              + '.'
            : 'Full transfer will move the covering agent to the absence campaign.'}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTransferConfirmOpen(false)}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={handleConfirmFullTransfer} disabled={loading}>
          Confirm Full Transfer
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CoverageAssignModal;
